import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  sortProposals,
  type ApprovalDecision,
  type ApprovalsScope,
  type ProposalSortKey,
  type SortDirection,
} from "../data/cockpit-data";
import { invalidateAfterMutation, useApprovalsPage, useConfig } from "../data/queries";
import { EmptyCard, QueryErrorState, TabSkeleton } from "./tab-states";
import { ProposalCard } from "./ProposalCard";
import { QueuePager } from "./QueuePager";
import { TableControls, type TableSelectControl } from "./TableControls";

/**
 * A proposal queue, paged and filtered by the SERVER.
 *
 * The filters, the facets and the count all come from `/state/approvals`, so a
 * tab downloads one page instead of the whole two-window payload. Sorting is
 * the one exception and deliberately so: the endpoint has no sort parameter, so
 * it is a reorder of the fetched page under the server's contract ordering
 * (undecided first, then domino, then recency) — which is why changing the sort
 * does not reset the page.
 */
const PAGE_SIZE = 50;
const TEXT_DEBOUNCE_MS = 300;

const SORT_OPTIONS: ReadonlyArray<{ value: `${ProposalSortKey}:${SortDirection}`; label: string }> = [
  { value: "impact:desc", label: "Impact high to low" },
  { value: "impact:asc", label: "Impact low to high" },
  { value: "domino:asc", label: "Domino A to Z" },
  { value: "domino:desc", label: "Domino Z to A" },
  { value: "market:asc", label: "Market A to Z" },
  { value: "market:desc", label: "Market Z to A" },
  { value: "status:asc", label: "Status A to Z" },
  { value: "status:desc", label: "Status Z to A" },
];

// "Undecided" is the default so the queue opens on the items that still need a
// call; the rest let an operator revisit what they already decided.
const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "proposed", label: "Undecided" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "deferred", label: "Deferred" },
  { value: "executed", label: "Published" },
  { value: "failed", label: "Failed" },
];

// The action a proposal recommends (the cockpit's `recommended_action`). Known
// codes get a plain label; an unknown one falls back to its code in sentence case
// so a new action still appears in the filter the day the machine starts emitting it.
const ACTION_LABELS: Record<string, string> = {
  EXCLUDE_EXACT: "Add exact negative",
  DEFER: "Defer (no action yet)",
  SET_OBJECTIVE: "Set objective",
};

function actionLabel(code: string): string {
  if (ACTION_LABELS[code]) return ACTION_LABELS[code];
  const words = code.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function ServerFilterableQueue({
  scope,
  emptyTitle = "No proposals match these filters.",
  showStatusFilter = false,
  defaultStatus = "",
  errorTitle = "Could not load the queue",
}: {
  scope: ApprovalsScope;
  emptyTitle?: string;
  showStatusFilter?: boolean;
  defaultStatus?: string;
  errorTitle?: string;
}) {
  const [domino, setDomino] = useState("");
  const [market, setMarket] = useState("");
  const [action, setAction] = useState("");
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [sort, setSort] = useState<`${ProposalSortKey}:${SortDirection}`>("impact:desc");
  const [offset, setOffset] = useState(0);
  // Inline decisions taken this session, overlaid on the server status so a
  // just-decided item leaves the "Undecided" filter without a reload.
  const [decisions, setDecisions] = useState<Record<string, ApprovalDecision>>({});
  const queryClient = useQueryClient();
  const config = useConfig();

  useEffect(() => {
    const handle = setTimeout(() => {
      setQ(text.trim());
      setOffset(0);
    }, TEXT_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [text]);

  const page = useApprovalsPage({
    scope,
    status: showStatusFilter && statusFilter ? statusFilter : undefined,
    domino: domino || undefined,
    market: market || undefined,
    action: action || undefined,
    q: q || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const visible = useMemo(() => {
    const [key, dir] = sort.split(":") as [ProposalSortKey, SortDirection];
    const overlaid = (page.data?.items ?? []).map((proposal) =>
      decisions[proposal.proposal_id] ? { ...proposal, status: decisions[proposal.proposal_id] } : proposal,
    );
    const active =
      showStatusFilter && statusFilter
        ? overlaid.filter((proposal) => proposal.status === statusFilter)
        : overlaid;
    return sortProposals(active, key, dir);
  }, [decisions, page.data, sort, statusFilter, showStatusFilter]);

  if (page.isPending) return <TabSkeleton />;
  if (page.isError) return <QueryErrorState title={errorTitle} onRetry={() => page.refetch()} />;

  const facets = page.data.facets;
  const total = page.data.total;

  const selects: TableSelectControl[] = [
    ...(showStatusFilter
      ? [
          {
            id: "status",
            label: "Status",
            value: statusFilter,
            allLabel: "All statuses",
            options: [...STATUS_OPTIONS],
          } satisfies TableSelectControl,
        ]
      : []),
    { id: "domino", label: "Domino", value: domino, options: facets.dominoes.map((value) => ({ label: value, value })) },
    { id: "market", label: "Market", value: market, options: facets.markets.map((value) => ({ label: value, value })) },
    {
      id: "action",
      label: "Action",
      value: action,
      options: facets.actions.map((value) => ({ label: actionLabel(value), value })),
    },
    { id: "sort", label: "Sort", value: sort, includeAll: false, options: [...SORT_OPTIONS] },
  ];

  function updateSelect(id: string, value: string) {
    if (id === "status") {
      setStatusFilter(value);
    } else if (id === "domino") {
      setDomino(value);
    } else if (id === "market") {
      setMarket(value);
    } else if (id === "action") {
      setAction(value);
    } else if (id === "sort" && isSortValue(value)) {
      setSort(value);
      return; // sorting is within-page; keep the current page
    }
    setOffset(0);
  }

  return (
    <div className="flex flex-col gap-4">
      <TableControls
        selects={selects}
        textValue={text}
        textPlaceholder="Keyword, target group, why"
        onSelectChange={updateSelect}
        onTextChange={setText}
        resultCount={total}
        countNoun="proposals"
      />
      {visible.length === 0 ? (
        <EmptyCard label={emptyTitle} />
      ) : (
        <div className={`flex flex-col gap-3 ${page.isFetching ? "opacity-60" : ""}`}>
          {visible.map((proposal) => (
            <ProposalCard
              key={proposal.proposal_id}
              proposal={proposal}
              campaignTemplateDefault={config.data?.campaign_template_default}
              campaignTemplates={config.data?.campaign_templates}
              onDecided={(id, decision) => {
                setDecisions((prev) => ({ ...prev, [id]: decision }));
                void invalidateAfterMutation(queryClient);
              }}
              onNoteSaved={() => void invalidateAfterMutation(queryClient)}
            />
          ))}
        </div>
      )}
      <QueuePager
        offset={offset}
        pageSize={PAGE_SIZE}
        total={total}
        busy={page.isFetching}
        onOffset={setOffset}
      />
    </div>
  );
}

function isSortValue(value: string): value is `${ProposalSortKey}:${SortDirection}` {
  return SORT_OPTIONS.some((option) => option.value === value);
}
